import type {
  Merge,
  OverrideProperties,
  SetOptional,
  Simplify,
} from 'type-fest'

import {
  type FileArea,
  OrganizationId,
  type OrganizationType,
  SessionId,
  TeamId,
  type UserDomainSelectModel,
  UserId,
  type UserPrincipalDomainQueryModel,
} from '@mntn-dev/domain-types'
import type { AuthSessionData } from './auth-session-data.ts'
import type { AuthUser } from './auth-user.ts'

/**
 * Session (aka AuthenticatedSession that we interface-merge with auth0 below)
 */

type AuthenticatedSessionPrincipalUser = {
  userId: UserId
  emailAddress: UserDomainSelectModel['emailAddress']
  teamIds?: TeamId[]
}

type AuthenticatedSessionPrincipal = {
  user: AuthenticatedSessionPrincipalUser
  scopes: string[]
}

type AuthenticatedSession = Simplify<
  SetOptional<
    OverrideProperties<
      AuthSessionData,
      { user: SetOptional<AuthUser, 'sub'> | Record<string, never> }
    >,
    'internal' | 'tokenSet'
  > & {
    sessionId: SessionId
    principal: AuthenticatedSessionPrincipal
    accessTokenExpiresAt?: number
    accessToken?: string
    organizationId?: string
  }
>

/**
 * UnauthenticatedSession
 */

type UnauthenticatedSession =
  | OverrideProperties<
      SetOptional<
        AuthenticatedSession,
        'principal' | 'internal' | 'tokenSet' | 'user'
      >,
      { user?: SetOptional<AuthUser, 'sub'> | Record<string, never> }
    >
  | null
  | undefined

/**
 * AuthorizedSession
 */

type Permissions = {
  canAcceptOffer: () => boolean
  canAdministerPackages: () => boolean
  canAdministerProjects: () => boolean
  canAdministerUsers: () => boolean
  canAdministerWatches: () => boolean
  canArchiveProject: () => boolean
  canCommentOnProject: () => boolean
  canCompleteProject: () => boolean
  canCreateActivity: () => boolean
  canCreateProject: () => boolean
  canCreateReview: () => boolean
  canCreateWatch: () => boolean
  canDeclineOffer: () => boolean
  canDeleteProject: () => boolean
  canDeleteWatch: () => boolean
  canEditAgency: () => boolean
  canEditMatch: () => boolean
  canEditProject: () => boolean
  canRemoveMatch: () => boolean
  canSubmitProject: () => boolean
  canSubscribeToInternalUpdates: () => boolean
  canUpdateReviewFeedback: () => boolean
  canUpdateReviewProposal: () => boolean
  canUpdateWatch: () => boolean
  canUploadFile: (fileType: FileArea) => boolean
  canViewActivity: () => boolean
  canViewAgency: () => boolean
  canViewAllWatches: () => boolean
  canViewPackages: () => boolean
  canViewBrand: () => boolean
  canViewInternalPricingNote: () => boolean
  canViewMatch: () => boolean
  canViewOffer: () => boolean
  canViewProject: () => boolean
  canViewWatch: () => boolean
  canViewCost: () => boolean
  canViewCostPlusMargin: () => boolean

  // Organizations
  canAdministerCustomerOrganizations: () => boolean // Can you administer any customer organization? For internal admins
  canAdministerOwnOrganization: () => boolean // Can you administer your own organization? For organization admins and internal super admins

  // Teams
  canAdministerCustomerTeams: () => boolean // Can you administer any customer teams? For internal admins
  canAdministerOwnOrganizationTeams: () => boolean // Can you administer any team in your organization? For organization admins
  canAdministerOwnTeam: () => boolean // Can you administer the team that you're on? For team admins
}

type WithPermissions<T> = Merge<T, Permissions>

type AuthorizedSessionJSON = OverrideProperties<
  AuthenticatedSession,
  {
    sessionId: SessionId
    principal: {
      user: Pick<
        UserPrincipalDomainQueryModel,
        'userId' | 'emailAddress' | 'teamIds' | 'organizationId'
      > & {
        // This field is our "role" in the short-term until we build out a full RBAC system
        organizationType: OrganizationType | 'system'
      }
      scopes: string[]
    }
  }
>

type AuthorizedSession = OverrideProperties<
  AuthorizedSessionJSON,
  {
    principal: WithPermissions<AuthorizedSessionJSON['principal']>
  }
>

/**
 * System Session
 */
type SystemSessionJson = OverrideProperties<
  AuthorizedSession,
  {
    sessionId: SessionId
    principal: {
      user: Pick<
        UserPrincipalDomainQueryModel,
        'userId' | 'emailAddress' | 'organizationId' | 'teamIds'
      > & {
        organizationType: 'system'
      }
      scopes: string[]
    }
  }
>

type SystemSession = OverrideProperties<
  AuthorizedSession,
  {
    principal: WithPermissions<SystemSessionJson['principal']>
  }
>

/**
 * Example of AuthorizedSession below
 */
const permissions = { canViewProject: () => true } as Permissions

const _authorizedSessionExample: AuthorizedSession = {
  sessionId: SessionId(),
  principal: {
    user: {
      userId: UserId(),
      emailAddress: 'test@mountain.com',
      teamIds: [TeamId()],
      organizationId: OrganizationId(),
      organizationType: 'brand',
    },
    scopes: [],
    ...permissions,
  },
  user: {
    sub: '',
  },
}

export type {
  UnauthenticatedSession,
  AuthenticatedSession,
  AuthenticatedSessionPrincipal,
  AuthenticatedSessionPrincipalUser,
  AuthorizedSession,
  AuthorizedSessionJSON,
  SystemSession,
  SystemSessionJson,
  Permissions,
}
